import React, { useState } from "react";
import { useAtom } from 'jotai';
import { allCarsAtom } from "../atoms/allCarsAtom";
import { carLocationsAtom } from "../atoms/carLocationsAtom";
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import { Chip, TableSortLabel } from "@mui/material";
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Car from "../atoms/allCarsAtom";
import { statusColors, statusOrder } from "../atoms/allCarsAtom";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'dimgrey',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': { 
    border: 0,
  },
}));

export default function AllCarUserTable() {
  const [allCarState, ] = useAtom(allCarsAtom);
  const [carLocations] = useAtom(carLocationsAtom);
  const [orderBy, setOrderBy] = useState<keyof Car>('CarNumber');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const headers = [
    "CarNumber", "Institution", "Status", "LastReception", "UID",
  ];

  const handleSort = (property: keyof Car) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedCars = React.useMemo(() => {
    if (!allCarState) return [];
    
    return [...allCarState].sort((a, b) => {
      if (orderBy === 'Status') {
        const aStatus = a.Status;
        const bStatus = b.Status;
        
        // Handle undefined/null status values
        if (!aStatus && !bStatus) return 0;
        if (!aStatus) return 1;  // Place undefined status at the end
        if (!bStatus) return -1; // Place undefined status at the end
        
        const aValue = statusOrder[aStatus as keyof typeof statusOrder] || 0;
        const bValue = statusOrder[bStatus as keyof typeof statusOrder] || 0;
        
        if (order === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else {
        const aValue = a[orderBy] ?? '';
        const bValue = b[orderBy] ?? '';

        if (order === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      }
    });
  }, [allCarState, orderBy, order]);

  return (
    <TableContainer component={Paper}>
      <Table stickyHeader size="small" aria-label="dense table">
        <TableHead>
          <StyledTableRow>
            {headers.map(header => (
              <StyledTableCell key={header}>
                <TableSortLabel
                  active={orderBy === header}
                  direction={orderBy === header ? order : 'asc'}
                  onClick={() => handleSort(header as keyof Car)}
                >
                  {header}
                </TableSortLabel>
              </StyledTableCell>
            ))}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {sortedCars.map((car: Car, index: number) => (
            <StyledTableRow key={index}>
              {headers.map(header => {
                if (header === "Status") {
                  const status = car.Status as keyof typeof statusColors;
                  return (
                    <TableCell key={`${header}-${index}`}>
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          bgcolor: statusColors[status],
                          color: 'white'
                        }}
                      />
                    </TableCell>
                  );
                } else {
                  return (
                    <TableCell key={`${header}-${index}`}>
                      {car[header as keyof Car]}
                    </TableCell>
                  );
                }
              })}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}